import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { userApi } from '@/services/api';
import { useRealtimeEvent } from '@/hooks/useRealtime';
import { Search, Bell, Menu, X, User, LogOut, Settings, Bookmark, MessageSquare } from 'lucide-react';

export function Header() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isHydrated, logout } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const queryClient = useQueryClient();

  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => userApi.getNotifications(true).then(res => res.data.data),
    enabled: isAuthenticated && isHydrated, // Only fetch when auth is ready
    staleTime: 60_000,
  });

  useRealtimeEvent(
    'notification:new',
    () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    [queryClient]
  );

  const unreadCount = notificationsData?.unreadCount || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-wine-900 via-wine-800 to-wine-900 border-b border-wine-700 shadow-lg">
      <div className="flex items-center h-16">
        {/* Brand Name Tag - Fixed width to match sidebar (w-64 = 256px), centered */}
        <div className="w-64 flex-shrink-0 flex items-center justify-center px-4">
          <Link to="/" className="flex items-center justify-center">
            <img loading="lazy" decoding="async" 
              src="/tripnovac-nametag.jpeg" 
              alt="Tripnovac" 
              className="h-12 w-48 object-contain"
            />
          </Link>
        </div>

        {/* Rest of header content */}
        <div className="flex-1 flex items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Search */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-lg">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-wine-300" />
              <input
                type="text"
                placeholder="Search posts, experiences, businesses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-wine-800/50 border border-wine-600 rounded-lg text-white placeholder-wine-300 focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link to="/forums/create">
                  <Button variant="primary" size="sm">Create Post</Button>
                </Link>
                <Link to="/messages" className="relative p-2 text-wine-200 hover:text-gold-400 transition-colors">
                  <MessageSquare className="h-6 w-6" />
                </Link>
                <Link to="/notifications" className="relative p-2 text-wine-200 hover:text-gold-400 transition-colors">
                  <Bell className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gold-400 px-1 text-[10px] font-semibold text-wine-900">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2"
                  >
                    <div className="w-8 h-8 bg-gold-500/20 border-2 border-gold-500 rounded-full flex items-center justify-center">
                      {user?.avatarUrl ? (
                        <img loading="lazy" decoding="async" src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="h-5 w-5 text-gold-400" />
                      )}
                    </div>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-wine-200 py-1">
                      <Link
                        to={`/users/${user?.username}`}
                        className="flex items-center px-4 py-2 text-sm text-wine-800 hover:bg-wine-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="h-4 w-4 mr-2" /> Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-wine-800 hover:bg-wine-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings className="h-4 w-4 mr-2" /> Settings
                      </Link>
                      <Link
                        to="/bookmarks"
                        className="flex items-center px-4 py-2 text-sm text-wine-800 hover:bg-wine-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Bookmark className="h-4 w-4 mr-2" /> Bookmarks
                      </Link>
                      <hr className="my-1" />
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        <LogOut className="h-4 w-4 mr-2" /> Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Login/Register buttons - Always visible when not authenticated */
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-2 border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-wine-900 font-semibold px-4"
                  >
                    Log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="bg-gold-500 hover:bg-gold-400 text-wine-900 font-semibold px-4"
                  >
                    Sign up
                  </Button>
                </Link>
              </div>
            )}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 text-wine-200 hover:text-gold-400"
            >
              {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {showMobileMenu && (
        <div className="lg:hidden border-t border-wine-700 bg-wine-800 py-4 px-4">
          <form onSubmit={handleSearch} className="mb-4">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-wine-700 border border-wine-600 rounded-lg text-white placeholder-wine-300"
            />
          </form>
          <nav className="space-y-2">
            <Link to="/forums" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Forums</Link>
            <Link to="/visa-experiences" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Visa Experiences</Link>
            <Link to="/directory" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Directory</Link>
            <Link to="/deals" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Deals</Link>
            <Link to="/events" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Events</Link>
            <Link to="/pricing" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Pricing</Link>
          </nav>
          
          {/* Mobile Auth Buttons */}
          {!isAuthenticated && (
            <div className="mt-4 pt-4 border-t border-wine-700 space-y-2">
              <Link to="/login" onClick={() => setShowMobileMenu(false)}>
                <Button variant="ghost" size="sm" className="w-full text-white hover:bg-wine-700">Log in</Button>
              </Link>
              <Link to="/register" onClick={() => setShowMobileMenu(false)}>
                <Button variant="secondary" size="sm" className="w-full">Sign up</Button>
              </Link>
            </div>
          )}
          
          {/* Mobile Authenticated Menu */}
          {isAuthenticated && user && (
            <div className="mt-4 pt-4 border-t border-wine-700 space-y-2">
              <Link to="/forums/create" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Create Post</Link>
              <Link to="/messages" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Messages</Link>
              <Link to={`/users/${user.username}`} className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Profile</Link>
              <Link to="/settings" className="block px-4 py-2 text-wine-100 hover:bg-wine-700 rounded-lg" onClick={() => setShowMobileMenu(false)}>Settings</Link>
              <button 
                onClick={() => { logout(); setShowMobileMenu(false); }}
                className="block w-full text-left px-4 py-2 text-red-400 hover:bg-wine-700 rounded-lg"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
